/**
 * sessionStepInheritance — what a session may do with S1–S4, given its track.
 *
 * Owner ruling (2026-07-27). The prelim steps are not "ask the user" or
 * "inherit silently" — each resolves independently against the track:
 *
 *   set on the track AND locked      → read-only here. You can't do anything.
 *   set on the track AND changeable  → visible AND editable here.
 *   not set on the track             → configurable here.
 *
 * S5 (Ranking) is the main event and always belongs to the session.
 *
 * The four track locks map one-to-one onto the four prelims:
 *   S1 Participants ← track.participationLockedAt
 *   S2 Items        ← item pool lock (poolLockedAt / pool_locked_at)
 *   S3 Criteria     ← track.criteriaLockedAt
 *   S4 Values       ← track.valuesConvergedAt
 *
 * Build contract §2.4a: inheritance must be inspectable at the point of use —
 * a surface that silently skips these steps (as the phone session wizard did)
 * hides the chain and is a defect.
 */

import type { StepId } from '@/features/session-engine-v3/types';

/** The four prelim steps. S5 is always session-owned. */
export type PrelimStepId = Extract<StepId, 'S1' | 'S2' | 'S3' | 'S4'>;

export const PRELIM_STEPS: PrelimStepId[] = ['S1', 'S2', 'S3', 'S4'];

export type StepDisposition =
  /** Set on the track and locked — read-only at the session. */
  | 'locked'
  /** Set on the track but still changeable — show it, allow a session override. */
  | 'inherited'
  /** Not set on the track — the session configures it. */
  | 'unset';

export interface StepSource {
  /** Does the track carry content for this step (participants, items, …)? */
  hasTrackContent: boolean;
  /** The track's lock timestamp for this step, when locked. */
  lockedAt: Date | string | null | undefined;
}

export interface StepResolution {
  step: PrelimStepId;
  label: string;
  disposition: StepDisposition;
  /** True when the session may write this step (unset or inherited-unlocked). */
  editableAtSession: boolean;
  /** One line for the UI — never leave the user guessing why a step is fixed. */
  reason: string;
}

const STEP_LABEL: Record<PrelimStepId, string> = {
  S1: 'Participants',
  S2: 'Items',
  S3: 'Criteria',
  S4: 'Values',
};

/** No track (standalone session): every prelim is the session's to configure. */
export const STANDALONE_SOURCE: StepSource = { hasTrackContent: false, lockedAt: null };

export function resolveStep(step: PrelimStepId, source: StepSource): StepResolution {
  const label = STEP_LABEL[step];
  if (source.lockedAt) {
    return {
      step,
      label,
      disposition: 'locked',
      editableAtSession: false,
      reason: `Locked on the track — ${label.toLowerCase()} can't change here.`,
    };
  }
  if (source.hasTrackContent) {
    return {
      step,
      label,
      disposition: 'inherited',
      editableAtSession: true,
      reason: `From the track — you can change ${label.toLowerCase()} for this session.`,
    };
  }
  return {
    step,
    label,
    disposition: 'unset',
    editableAtSession: true,
    reason: `Not set on the track — set ${label.toLowerCase()} for this session.`,
  };
}

export function resolveSessionSteps(
  sources: Record<PrelimStepId, StepSource>,
): Record<PrelimStepId, StepResolution> {
  return {
    S1: resolveStep('S1', sources.S1),
    S2: resolveStep('S2', sources.S2),
    S3: resolveStep('S3', sources.S3),
    S4: resolveStep('S4', sources.S4),
  };
}

/** Every prelim a standalone (track-less) session must configure itself. */
export function resolveStandaloneSteps(): Record<PrelimStepId, StepResolution> {
  return resolveSessionSteps({
    S1: STANDALONE_SOURCE,
    S2: STANDALONE_SOURCE,
    S3: STANDALONE_SOURCE,
    S4: STANDALONE_SOURCE,
  });
}

/** What a track holds for S1–S4, as read from its lock columns and rows. */
export interface TrackLockState {
  participationLockedAt: string | null;
  poolLockedAt: string | null;
  criteriaLockedAt: string | null;
  valuesConvergedAt: string | null;
  rangeSetLockedAt: string | null;
  participantCount: number;
  poolItemCount: number;
  criteriaCount: number;
}

/**
 * The four prelims for a track-bound session. S4's content signal is the range
 * lock and its lock is values converged, as on iPhone (TrackLockState.resolutions
 * in SessionStepInheritance.swift).
 */
export function resolveTrackSteps(state: TrackLockState): Record<PrelimStepId, StepResolution> {
  return resolveSessionSteps({
    S1: { hasTrackContent: state.participantCount > 0, lockedAt: state.participationLockedAt },
    S2: { hasTrackContent: state.poolItemCount > 0, lockedAt: state.poolLockedAt },
    S3: { hasTrackContent: state.criteriaCount > 0, lockedAt: state.criteriaLockedAt },
    S4: { hasTrackContent: state.rangeSetLockedAt !== null, lockedAt: state.valuesConvergedAt },
  });
}
